<template>
  <Loader :value="!Boolean(data)" :delay="0" theme="default" class="pricing-card-lines">
    <div class="pricing-card-lines__summary">
      <component v-for="(line, index) in totalData" :value="line" :is="line.type" :key="'total' + index">
        <div v-if="index === 0" class="row g-2">
          <b class="pricing-card-lines__summary-title">
            {{ $t('price') }}
            <span style="margin-top: 5px; font-weight: normal; opacity: 0.5; display: block; font-size: 0.7rem">{{
              $t('excludingVat')
            }}</span>
          </b>
          <ButtonComponent
            v-if="data.length > 1"
            :icon="['fal', details ? 'angle-up' : 'angle-down']"
            @click="details = !details"
          />
        </div>
      </component>
    </div>
    <div v-if="details && data.length > 1" class="pricing-card-lines__details">
      <component v-for="(line, index) in data" :value="line" :is="line.type" :key="'detail' + index">
        <template #default>
          <b>{{ metas[line.item] && metas[line.item].name }}</b>
          <small style="display: block">
            {{
              $t('orderConfirmationConditionShort', {
                lockInPeriod: lockInDate(line.conditions.subscription).fromNow(true),
                noticePeriod: noticePeriod(line.conditions.subscription).fromNow(true),
              })
            }}</small
          >
        </template>
      </component>
    </div>
  </Loader>
</template>

<script>
import Card from '@/components/Card'
import RootAPI from '@/api/Root'
import Lines from './Lines'
import moment from 'moment'

function parsePeriod(period) {
  return { value: period.substr(0, period.length - 1), unit: period.substr(-1) }
}

export default {
  props: {
    value: {
      type: [String, Array],
      required: true,
    },
    options: Object,
  },
  watch: {
    allData: {
      handler: 'fetch',
      immediate: true,
      deep: true,
    },
  },
  data() {
    return {
      rawData: null,
      promise: null,
      details: false,
    }
  },
  computed: {
    customSubscriptionPricing() {
      return this.$store.state.enums.CustomSubscriptionPricing
    },
    totalData() {
      if (!this.data) return []

      const hasCustomSubscription = this.data.some(d => d.type === 'custom')

      return Object.entries(
        this.data.reduce((res, cur) => {
          let type = hasCustomSubscription ? 'custom' : cur.type
          if (!(type in res)) res[type] = { net: 0, gross: 0 }

          res[type].net += cur.net
          res[type].gross += cur.gross

          return res
        }, {}),
      ).map(([type, { gross, net }]) => ({ type, net, gross }))
    },
    allData() {
      return {
        items: this.items,
        options: this.options,
      }
    },
    isArray() {
      return this.value instanceof Array
    },
    data() {
      if (!this.rawData) return []
      return this.rawData
        .flat()
        .map(r =>
          r.price.map(p => {
            return {
              ...p,
              item: r.source.item,
              net: ((100 - (p.discount || 0)) / 100) * p.value,
              gross: p.value,
              type: this.customSubscriptionPricing.includes(r.source.item) ? 'custom' : p.type,
            }
          }),
        )
        .flat()
    },
    items() {
      return this.isArray ? this.value : [this.value]
    },
    meta() {
      return this.metas[this.items[0]] || {}
    },
    metas() {
      const meta = this.$store.state.enums.ProductMeta
      return Object.fromEntries(this.items.map(i => [i, meta[i]]))
    },
  },
  methods: {
    fetch() {
      this.rawData = null
      this.promise = RootAPI.getPrice(this.items, this.options)
        .then(data => (this.rawData = data))
        .then(data => {
          const findHighestPeriod = period => {
            let current = '1M'

            data.forEach(addon => {
              addon.price.forEach(price => {
                let sub = price.conditions.subscription[period]

                if (parseInt(parsePeriod(sub).value) > parseInt(parsePeriod(current).value)) current = sub
              })
            })

            return current
          }

          const subscription = {
            noticePeriod: findHighestPeriod('noticePeriod'),
            lockInPeriod: findHighestPeriod('lockInPeriod'),
          }

          return this.$emit('subscription', subscription)
        })
    },
    lockInDate(value) {
      let lockInPeriod = parsePeriod(value.lockInPeriod)
      return moment().add(lockInPeriod.value, lockInPeriod.unit)
    },
    noticePeriod(value) {
      let noticePeriod = parsePeriod(value.noticePeriod)
      return moment().add(noticePeriod.value, noticePeriod.unit)
    },
  },
  components: {
    Card,
    ...Lines,
  },
}
</script>

<style lang="scss">
.pricing-card-lines {
  flex-grow: 1;

  &.elder-loader--active {
    justify-content: flex-start;
  }

  .elder-loader__element-content-inner {
    margin: 0;
    padding: 0;
    text-align: left;
    min-width: initial;
    display: flex;
    align-items: center;
  }

  .elder-loader__loading-message {
    margin: 0 0 0 0.5rem;
  }

  &__summary {
    &-title {
      font-size: 1em;
      font-weight: bold;
      margin: 0;
      line-height: 1;
    }

    .elder-button {
      align-self: center;

      .elder-button__icon {
        padding: 0 0.5rem !important;
      }
    }
  }

  &__details {
    border-top: 2px solid var(--border-color);
    padding-top: 1rem;
    margin-top: 1rem;
    small {
      opacity: 0.7;
      font-size: 0.7rem;
    }
  }

  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__info {
    flex-grow: 1;
  }

  &__title {
    font-weight: bold;
    text-align: center;
  }

  &__description {
    text-align: center;
    margin-top: 0.5rem;
  }

  &-line {
    text-align: right;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &__value {
      font-size: 1.4em;
      display: inline-block;
      flex-shrink: 0;
      min-width: 100px;
    }

    &__type {
      opacity: 0.75;
      margin-right: 0.5rem;
    }

    &__net {
      font-weight: bold;
    }

    &__gross {
      text-decoration: line-through;
      opacity: 0.5;
      margin-right: 0.5rem;
    }

    &__item {
      margin-right: auto;
      padding-right: 1rem;
      flex-grow: 1;
      text-align: left;
    }

    & + & {
      border-top: 1px dashed var(--border-color);
      margin-top: 5px;
      padding-top: 5px;

      // &:before {
      //   content: '+';
      //   display: inline-block;
      //   margin-right: 0.25rem;
      //   font-weight: bold;
      //   font-size: 1em;
      // }
    }
  }
}
</style>
